/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */

import { Box, IconButton, useTheme } from "@mui/material";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components";
import { ContentCopyIcon, TimelineIcon } from "@/shared/foundation/icons/mui";
import { HttpResponse, RobotoDomainException } from "@/shared/http";

export interface ResponseExplorerProps {
  response?: HttpResponse;
  responseBody?: Body | RobotoDomainException;
}

export const ResponseExplorer: React.FC<ResponseExplorerProps> = ({
  response,
  responseBody,
}) => {
  const theme = useTheme();

  if (!response || !responseBody) {
    return <></>;
  }

  const formattedResponseBody = JSON.stringify(responseBody, null, 2);
  const xrayIdx = formattedResponseBody.search("https");
  const xray =
    xrayIdx < 0
      ? undefined
      : formattedResponseBody.substring(xrayIdx, xrayIdx + 121);

  let responseBodyPart = <></>;
  if (responseBody) {
    responseBodyPart = (
      <>
        <h4>Body</h4>
        <Box
          sx={{
            whiteSpace: "pre-wrap",
            color: theme.palette.text.secondary,
            fontSize: "0.9rem",
            marginTop: theme.spacing(1),
            padding: theme.spacing(1.5),
            backgroundColor: theme.palette.code.main,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: theme.spacing(5),
            }}
          >
            <RobotoTooltip title={"Copy to clipboard"}>
              <IconButton
                onClick={() => {
                  void navigator.clipboard.writeText(formattedResponseBody);
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </RobotoTooltip>
          </Box>
          <code
            data-cy="token-code"
            style={{
              color: theme.palette.text.primary,
              wordBreak: "break-all",
              whiteSpace: "pre",
            }}
          >
            {formattedResponseBody}
          </code>
        </Box>
      </>
    );
  }

  return (
    <>
      <h4>Status</h4>
      <p>{response.status}</p>
      {xray && (
        <IconButton href={xray} target={"_blank"}>
          <TimelineIcon fontSize="small" />
        </IconButton>
      )}
      {responseBodyPart}
    </>
  );
};
